// React, Next
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import BeforeAfterSlider from '@/components/sections/BeforeAfterSlider'
import { GlobalContext } from 'context/GlobalContextProvider'

// Img
import BeforeImage from 'public/img/services/machine_before_retrofit.webp'
import AfterImage from 'public/img/services/retrofited_machine_after.webp'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBodySmall } from '@/assets/theme/fonts'

type Scenario = {
  id: string
  title: string
  before: string
  after: string
}

export default function ScenariosSection() {
  const { t } = useTranslation('servicesTestEquipmentModernization')
  const { screenDimensions } = useContext(GlobalContext)

  const stacked = screenDimensions.width < breakpoints.desktopToMobile

  const scenarios: Scenario[] = [
    {
      id: '01',
      title: t('scenarios.items.01.title'),
      before: t('scenarios.items.01.before'),
      after: t('scenarios.items.01.after')
    },
    {
      id: '02',
      title: t('scenarios.items.02.title'),
      before: t('scenarios.items.02.before'),
      after: t('scenarios.items.02.after')
    },
    {
      id: '03',
      title: t('scenarios.items.03.title'),
      before: t('scenarios.items.03.before'),
      after: t('scenarios.items.03.after')
    },
    {
      id: '04',
      title: t('scenarios.items.04.title'),
      before: t('scenarios.items.04.before'),
      after: t('scenarios.items.04.after')
    }
  ]

  /** A scenario as text only - the photographs above are one real project. */
  const getScenario = (scenario: Scenario) => (
    <Box
      key={scenario.id}
      sx={{
        background: themeColors.kroemtech.background,
        border: `1px solid ${themeColors.gray.lighter}`,
        borderRadius: '10px',
        padding: '1.75rem',
        boxSizing: 'border-box',
        height: '100%'
      }}
    >
      <Typography
        variant="h3"
        component="h3"
        sx={{
          fontSize: '1.05rem',
          fontWeight: 700,
          lineHeight: 1.3,
          color: themeColors.kroemtech.dunkelblau,
          marginBottom: '1.1rem'
        }}
      >
        {scenario.title}
      </Typography>

      {[
        { label: t('scenarios.beforeLabel'), text: scenario.before, on: false },
        { label: t('scenarios.afterLabel'), text: scenario.after, on: true }
      ].map((row, i) => (
        <Box
          key={row.label}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem',
            marginBottom: i === 0 ? '0.9rem' : 0
          }}
        >
          <Typography
            component="span"
            sx={{
              flexShrink: 0,
              minWidth: '4.2rem',
              fontSize: '0.68rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              textAlign: 'center',
              color: '#fff',
              background: row.on
                ? themeColors.success.main
                : 'rgba(18, 17, 57, 0.6)',
              borderRadius: '4px',
              padding: '0.25rem 0.4rem',
              marginTop: '0.15rem'
            }}
          >
            {row.label}
          </Typography>
          <Typography
            sx={{
              fontSize: getFontBodySmall(),
              lineHeight: 1.6,
              color: themeColors.gray.defaultText
            }}
          >
            {row.text}
          </Typography>
        </Box>
      ))}
    </Box>
  )

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <SectionHeader
        eyebrow={t('scenarios.eyebrow')}
        title={t('scenarios.title')}
        intro={[t('scenarios.intro1'), t('scenarios.intro2')]}
      />

      {/* One real reference, shown as a before / after comparison. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: stacked ? '1fr' : '1fr 1fr',
          gap: stacked ? '2rem' : '3rem',
          alignItems: 'center',
          marginBottom: '3rem'
        }}
      >
        <BeforeAfterSlider
          before={BeforeImage}
          after={AfterImage}
          beforeLabel={t('scenarios.beforeLabel')}
          afterLabel={t('scenarios.afterLabel')}
          hint={t('scenarios.dragHint')}
        />

        <Box>
          <Typography
            sx={{
              fontSize: getFontBodySmall(),
              lineHeight: 1.7,
              color: themeColors.gray.defaultText
            }}
          >
            {t('scenarios.intro2')}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginTop: '1.5rem'
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#fff',
                background: 'rgba(18, 17, 57, 0.6)',
                borderRadius: '4px',
                padding: '0.25rem 0.6rem'
              }}
            >
              {t('scenarios.beforeLabel')}
            </Typography>
            <ArrowForwardIcon
              sx={{ fontSize: '1.2rem', color: themeColors.success.main }}
            />
            <Typography
              component="span"
              sx={{
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#fff',
                background: themeColors.success.main,
                borderRadius: '4px',
                padding: '0.25rem 0.6rem'
              }}
            >
              {t('scenarios.afterLabel')}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: stacked ? '1fr' : 'repeat(2, 1fr)',
          gap: '1.5rem',
          alignItems: 'stretch'
        }}
      >
        {scenarios.map(scenario => getScenario(scenario))}
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.section.light}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
